<script lang="ts" setup>
import { reactive, computed, watch } from 'vue';

import { useBroadcast } from '~/stores/broadcast.store';
import { codes } from '~/entities/phone-code';

const broadcastStore = useBroadcast();

const state = reactive({
  code: '+7',
  number: '',
  fullPhone: '',
});

const cancel = () => {
  broadcastStore.setVisiblePreviewModal(false);
  state.number = '';
  state.fullPhone = '';
};
const onSend = async () => {
  await broadcastStore.sendPreviewMsg(state.fullPhone);

  if (!broadcastStore.previewMessageError) cancel();
};

const isValidPhone = computed(() => {
  const code = state.code;
  const currentCode = codes.find((c) => c.code === code);
  const currentNumber = state.fullPhone.replace(/\+/g, '');

  return currentNumber.length === currentCode?.availableLength;
});
const hasInvalidFormat = computed(() => {
  return broadcastStore.previewMessageError.includes('phoneNumber must be a valid');
});

watch(state, () => {
  broadcastStore.setPreviewMsgError('');
});
</script>

<template>
  <el-dialog
    :model-value="broadcastStore.getVisiblePreviewModal"
    :show-close="false"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    destroy-on-close
    width="560px"
  >
    <div class="dialog">
      <div class="dialog-header">
        <p class="title">{{ $t('broadcast.modal.previewSend.title') }}</p>
      </div>

      <div class="dialog-info">
        <p class="description">{{ $t('broadcast.modal.previewSend.description') }}</p>

        <BasePhone
          v-model:code="state.code"
          v-model:number="state.number"
          v-model:phone-number="state.fullPhone"
          :is-invalid-format="hasInvalidFormat"
          class="form"
        />
      </div>

      <div class="dialog-footer">
        <el-button @click="cancel" class="cancel">
          {{ $t('broadcast.modal.cancel') }}
        </el-button>

        <el-button @click="onSend" :disabled="!isValidPhone || broadcastStore.isFetching" class="confirm">
          {{ $t('broadcast.modal.send') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
.dialog {
  padding: 32px 24px;
  box-sizing: border-box;

  &-header {
    .title {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      color: $typography-title;
    }
  }

  &-info {
    margin-top: 32px;
    width: 100%;

    .description {
      margin-bottom: 24px;
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: $typography-secondary-color;
      word-break: break-word;
    }

    :deep(.form) {
      .code {
        height: 58px;
        width: 106px;
        box-sizing: border-box;

        .current-option {
          width: 84px;
          box-sizing: border-box;

          &__text {
            font-size: 16px;
            color: $typography-paragraph;
          }
        }
      }

      .input-wrapper {
        .input-number {
          height: 58px;
          box-sizing: border-box;
        }
      }
    }
  }

  &-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;

    button {
      box-sizing: border-box;
      width: 120px;
      height: 36px;
      font-family: 'Roboto', sans-serif;
      letter-spacing: 0.89px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;

      &:disabled {
        opacity: .66;
      }
    }

    .confirm {
      border: none;
      color: $ui-white;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      text-transform: uppercase;
      background: $ui-accent;

      &:hover {
        color: $ui-white;
        background: $ui-accent;
      }
    }

    .cancel {
      color: $ui-accent;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      background: $ui-white;
      padding: 12px 16px;
      border: none;
      text-transform: uppercase;
    }
  }
}
</style>
